// pages/SettingsPage.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChefHat,
  ClipboardList,
  Cloud,
  CloudOff,
  Database,
  Download,
  Lock,
  LogOut,
  Package,
  RefreshCw,
  Settings,
  Trash2,
  Users,
  Wine,
  Wrench,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { getBusiness, getLocalDbInfo, hydrateFromSnapshot, verifyOfflineReadiness } from "@/lib/localDb";
import { pingConnectivity } from "@/lib/offlineAuth";
import {
  clearDeviceConfig,
  getDeviceId,
  getStoredBusinessCode,
  getStoredDeviceRole,
  isStandaloneLaunch,
  resolveLandingPathForDevice,
  setStoredDeviceRole,
} from "@/lib/device";
import { isServiceWorkerActive } from "@/lib/pwa";
import { API, DEV_SIMULATE_CLOUD_KEY, DEV_SIMULATE_OFFLINE_KEY, http } from "@/app/core";
import { ROLE_SESSION_KEYS, findOnlineRoleToken, forEachStoredSession, readRoleSession, removeRoleSession } from "@/lib/authStorage";


// Full logout helper — kept for the "Reset & reload" ErrorBoundary
// button. Wipes ALL role sessions unconditionally.
function _clearAllRoleSessions() {
  try {
    Object.keys(ROLE_SESSION_KEYS).forEach((role) => removeRoleSession(role, "both"));
  } catch (_) { /* noop */ }
}

/**
 * SettingsPage — PWA device management screen.
 *
 * Accessible via `/settings` and (once mounted) a small floating gear
 * button that appears on every authenticated dashboard. Surfaces:
 *   • App version (bumped alongside the service-worker CACHE_VERSION)
 *   • Device role, business code, restaurant name
 *   • Connection status (online / offline) via navigator.onLine
 *   • Current signed-in user (owner / staff / kitchen / bar / admin)
 *   • Actions: check for updates, clear cache, sign out
 *
 * This is intentionally a self-contained page — it doesn't touch any
 * dashboard state so it can be opened from anywhere without risking
 * data loss. All heavy state (sessions, device config) is read from
 * localStorage / sessionStorage on mount.
 */
const APP_VERSION = "1.0.6";

/**
 * LocalStatusCard — diagnostics surface for the "True Local Application"
 * milestone (Session 25). Renders a snapshot of everything the operator
 * or support needs to know about this device's offline readiness:
 *   • Offline Ready badge (from verifyOfflineReadiness)
 *   • Initial sync timestamp
 *   • Business version / snapshot id
 *   • Last cloud sync
 *   • Pending sync queue count (placeholder until Session 26)
 *   • Cloud connectivity
 *   • Local DB health (per-store record counts)
 *   • Actions: re-verify, re-run setup
 *
 * The card refreshes on mount, when the browser online/offline events
 * fire, and on manual "Re-verify" clicks.
 */
function LocalStatusCard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [cloudConnected, setCloudConnected] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  // Session 27 — display the actual restaurant name (e.g. "Spur
  // Steakhouse") alongside the business slug ("spur"). Read straight
  // from IndexedDB so this works offline.
  const [businessRecord, setBusinessRecord] = useState(null);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [info, ready, cloud, biz] = await Promise.all([
        getLocalDbInfo(),
        verifyOfflineReadiness(),
        pingConnectivity({ timeoutMs: 2500 }),
        getBusiness(),
      ]);
      setStatus(info);
      setReport(ready);
      setCloudConnected(!!cloud);
      setBusinessRecord(biz || null);
    } catch (_) { /* noop */ }
    finally { setRefreshing(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const on = () => refresh();
    const off = () => setCloudConnected(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [refresh]);

  const offlineReady = !!(report && report.ok);
  const installCompleted = Boolean(status?.installCompletedAt);
  const hasAnySession = useMemo(() => {
    try {
      let found = false;
      forEachStoredSession((_role, parsed) => { if (parsed?.token) { found = true; return true; } return false; }, { scope: "tab" });
      if (found) return true;
    } catch (_) { /* noop */ }
    return false;
    // `status` is the deliberate recompute trigger: sessionStorage is not
    // reactive, so we re-scan it whenever the install status changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  const businessSlug = status?.slug || null;
  const businessName = (businessRecord && businessRecord.name) || null;
  const businessDisplay = businessName || businessSlug || null;
  const lastSync = status?.lastSnapshotAt || null;
  const snapshotVersion = status?.counts ? (Number(status?.counts?.__version) || null) : null;
  const relTime = (iso) => {
    if (!iso) return "—";
    try {
      const then = new Date(iso).getTime();
      const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
      if (secs < 60) return `${secs}s ago`;
      if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
      if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
      return `${Math.floor(secs / 86400)}d ago`;
    } catch (_) { return iso; }
  };

  const coreRows = [
    { key: "categories", label: "Categories" },
    { key: "menu_items", label: "Products" },
    { key: "users",      label: "Users" },
    { key: "tables",     label: "Tables" },
    { key: "table_sessions", label: "Open sessions" },
    { key: "orders",     label: "Recent orders" },
    { key: "offline_credentials", label: "Offline PIN enrollments" },
  ];

  return (
    <Card className="rounded-[24px] border-border bg-card" data-testid="settings-local-status-card">
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold">Local status</p>
              <p className="text-xs text-muted-foreground">Diagnostics for this device's offline readiness.</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={refreshing}
            className="rounded-xl"
            data-testid="settings-local-status-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Re-verify
          </Button>
        </div>

        {/* Install status banner — visible when the device has not
            completed Stage 2 (business synchronisation). This is the
            single most common reason "Offline Ready" is No, so we
            surface it prominently with a direct call to action. */}
        {!installCompleted ? (
          <div
            className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4"
            data-testid="settings-install-required-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  Offline installation not yet completed
                </p>
                <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
                  {hasAnySession
                    ? "Your business data hasn't been downloaded to this device yet. Complete the setup below to make this device offline-ready."
                    : "Sign in online once to download your business's complete data package. After that, this device will work offline."}
                </p>
                <div className="mt-3">
                  <Button
                    size="sm"
                    className="rounded-xl"
                    onClick={() => {
                      if (hasAnySession) navigate("/install/setup?next=/settings");
                      else navigate("/");
                    }}
                    data-testid="settings-complete-install-cta"
                  >
                    <Download className="h-4 w-4" />
                    {hasAnySession ? "Complete offline installation" : "Sign in to complete installation"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Top-line status pills */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div
            className={`flex items-center justify-between rounded-2xl border px-3 py-2 ${offlineReady ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}
            data-testid="settings-offline-ready"
          >
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Offline ready</span>
            <span className={`flex items-center gap-1.5 text-sm font-semibold ${offlineReady ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
              {offlineReady ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {offlineReady ? "Yes" : "No"}
            </span>
          </div>
          <div
            className={`flex items-center justify-between rounded-2xl border px-3 py-2 ${cloudConnected ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}
            data-testid="settings-cloud-status"
          >
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Cloud</span>
            <span className={`flex items-center gap-1.5 text-sm font-semibold ${cloudConnected ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}`}>
              {cloudConnected ? <Cloud className="h-3.5 w-3.5" /> : <CloudOff className="h-3.5 w-3.5" />}
              {cloudConnected ? "Connected" : "Disconnected"}
            </span>
          </div>
        </div>

        {/* Detail grid */}
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-background/40 p-3">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Business</dt>
            <dd className="mt-1 text-sm font-semibold" data-testid="settings-business-name">
              {businessDisplay || "—"}
            </dd>
            {businessName && businessSlug && businessName.toLowerCase() !== businessSlug.toLowerCase() ? (
              <dd className="mt-0.5 font-mono text-[11px] text-muted-foreground" data-testid="settings-business-slug">
                {businessSlug}
              </dd>
            ) : null}
          </div>
          <div className="rounded-2xl border border-border bg-background/40 p-3">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Last cloud sync</dt>
            <dd className="mt-1 text-sm" data-testid="settings-last-sync">
              {lastSync ? (
                <>
                  {relTime(lastSync)}
                  <span className="ml-2 text-[10px] text-muted-foreground">({new Date(lastSync).toLocaleTimeString()})</span>
                </>
              ) : "Never"}
            </dd>
          </div>
          <div className="rounded-2xl border border-border bg-background/40 p-3">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Snapshot</dt>
            <dd className="mt-1 text-xs" data-testid="settings-snapshot-id">
              {status?.snapshotId ? (
                <>
                  <span className="font-mono">{status.snapshotId.slice(0, 8)}…</span>
                  {status.snapshotVersion != null ? (
                    <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium">
                      v{status.snapshotVersion}
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </dd>
          </div>
          <div className="rounded-2xl border border-border bg-background/40 p-3">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Pending sync queue</dt>
            <dd className="mt-1 text-sm">
              0 <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">(placeholder)</span>
            </dd>
          </div>
        </dl>

        {/* Per-store record counts */}
        <div className="rounded-2xl border border-dashed border-border p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Local database contents</p>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-3">
            {coreRows.map(({ key, label }) => {
              const count = status?.counts?.[key];
              return (
                <div key={key} className="flex justify-between gap-2" data-testid={`settings-store-${key}`}>
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-mono">{typeof count === "number" ? count : "—"}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Readiness check breakdown */}
        {report?.checks?.length ? (
          <details className="rounded-2xl border border-border bg-background/40 p-3 text-xs" data-testid="settings-readiness-details">
            <summary className="cursor-pointer select-none font-medium">
              Readiness checks ({report.checks.filter((c) => c.ok).length}/{report.checks.length} passing)
            </summary>
            <ul className="mt-2 space-y-1">
              {report.checks.map((c, i) => (
                <li key={i} className="flex items-start gap-2">
                  {c.ok
                    ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />}
                  <div>
                    <span className="font-mono text-[11px]">{c.name}</span>
                    {c.detail ? <span className="ml-1 text-muted-foreground">— {c.detail}</span> : null}
                  </div>
                </li>
              ))}
            </ul>
          </details>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="flex-1 rounded-xl"
            onClick={() => navigate("/install/setup?next=/settings")}
            data-testid="settings-rerun-install"
          >
            <Download className="h-4 w-4" />
            Re-run offline setup
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}



/**
 * DeveloperToolsCard — protected maintenance surface for support and
 * development. Locked behind a Developer PIN (temporarily 0511) so
 * normal restaurant users can never trigger a database wipe or a
 * simulated outage by accident.
 *
 * The unlock is session-scoped (sessionStorage) — closing the app
 * relocks the tools. Not a security boundary against a determined
 * attacker with dev-tools access, just a guardrail against accidental
 * damage in production.
 *
 * Tools provided:
 *   • Simulate Offline Mode       — reject every http request
 *   • Simulate Cloud Disconnect   — reject /api/* only
 *   • Force Cloud Synchronisation — pull a fresh snapshot right now
 *   • Rebuild Local Database      — wipe + resync
 *   • Re-run Offline Readiness    — full check report dialog
 *   • View Recent Event Log       — last 100 entries from event_log
 *   • Export Diagnostic Info      — download JSON blob for support
 */
const DEV_PIN = "0511"; // temporary; will be moved to server config later
const DEV_UNLOCK_KEY = "nfcs:dev:unlocked";

function DeveloperToolsCard({ onChanged }) {
  const navigate = useNavigate();
  // Session 25.7 — the entire Settings route is now behind a full-screen
  // PIN gate (see GatedSettingsPage), so this card no longer manages
  // its own lock. It is unconditionally interactive here.
  const [simulateOffline, setSimulateOffline] = useState(() => {
    try { return localStorage.getItem(DEV_SIMULATE_OFFLINE_KEY) === "1"; } catch (_) { return false; }
  });
  const [simulateCloud, setSimulateCloud] = useState(() => {
    try { return localStorage.getItem(DEV_SIMULATE_CLOUD_KEY) === "1"; } catch (_) { return false; }
  });
  const [busy, setBusy] = useState(null);
  const [eventLogOpen, setEventLogOpen] = useState(false);
  const [eventRows, setEventRows] = useState([]);
  const [readinessDialog, setReadinessDialog] = useState(null);
  // Session 43 — server-side crash reports from EVERY device of this business.
  const [crashOpen, setCrashOpen] = useState(false);
  const [crashData, setCrashData] = useState(null);
  const [crashExpanded, setCrashExpanded] = useState(null);

  const notifyChange = () => { if (typeof onChanged === "function") onChanged(); };

  const openCrashReports = async () => {
    setBusy("crash");
    try {
      const token = findOnlineToken();
      const r = await http.get("/owner/diagnostics/client-errors", { headers: token ? { Authorization: `Bearer ${token}` } : {}, params: { limit: 200 } });
      let local = [];
      try { local = JSON.parse(window.localStorage.getItem("nfcs.errors") || "[]"); } catch (_) { local = []; }
      setCrashData({ ...(r.data || {}), local });
      setCrashOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load crash reports (sign in as owner online).");
    } finally { setBusy(null); }
  };
  const clearCrashReports = async () => {
    try {
      const token = findOnlineToken();
      await http.delete("/owner/diagnostics/client-errors", { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      try { window.localStorage.removeItem("nfcs.errors"); } catch (_) { /* noop */ }
      setCrashData({ errors: [], count_24h: 0, by_kind: {}, local: [] });
      toast.success("Crash reports cleared.");
    } catch (e) { toast.error("Couldn't clear."); }
  };

  const toggleSimulateOffline = (next) => {
    try {
      if (next) localStorage.setItem(DEV_SIMULATE_OFFLINE_KEY, "1");
      else localStorage.removeItem(DEV_SIMULATE_OFFLINE_KEY);
    } catch (_) { /* noop */ }
    setSimulateOffline(next);
    toast.message(next ? "Simulated offline enabled" : "Simulated offline disabled", {
      description: next
        ? "All cloud calls will be rejected until you turn this off."
        : "Cloud communication restored.",
    });
    notifyChange();
  };
  const toggleSimulateCloud = (next) => {
    try {
      if (next) localStorage.setItem(DEV_SIMULATE_CLOUD_KEY, "1");
      else localStorage.removeItem(DEV_SIMULATE_CLOUD_KEY);
    } catch (_) { /* noop */ }
    setSimulateCloud(next);
    toast.message(next ? "Simulated cloud disconnect enabled" : "Simulated cloud disconnect disabled");
    notifyChange();
  };

  // Retrieve any online session token for /sync/snapshot calls.
  // Session 27.23 — checks BOTH sessionStorage and localStorage. In
  // some flows (owner/admin "stay signed in") the session is persisted
  // to localStorage instead of sessionStorage; the old lookup ignored
  // those and always kicked the user back to the login screen even
  // though a valid online token was sitting in localStorage under
  // the same key.
  const findOnlineToken = () => {
    try { return findOnlineRoleToken({ scope: "any" }); } catch (_) { return null; }
  };

  const forceSync = async () => {
    setBusy("sync");
    try {
      const token = findOnlineToken();
      if (!token) {
        // Session 27.23 — Clearer, more actionable error. The user was
        // seeing this from inside the Dev Tools panel (which they've
        // already unlocked with the PIN) and it confusingly said "Sign
        // in via the login screen first" as if the whole app was
        // signed out. In fact only the online session had expired —
        // they can just re-login their existing role in a new tab.
        toast.error("Online session required", {
          description: "Open a role login (owner/staff/kitchen/bar) and sign in, then return here and tap Rebuild again. Your offline session is still intact.",
        });
        return;
      }
      // Temporarily disable simulate-offline so the sync actually runs.
      const wasOffline = localStorage.getItem(DEV_SIMULATE_OFFLINE_KEY) === "1";
      const wasCloud = localStorage.getItem(DEV_SIMULATE_CLOUD_KEY) === "1";
      if (wasOffline || wasCloud) {
        try { localStorage.removeItem(DEV_SIMULATE_OFFLINE_KEY); } catch (_) { /* noop */ }
        try { localStorage.removeItem(DEV_SIMULATE_CLOUD_KEY); } catch (_) { /* noop */ }
      }
      // Session 27.33 — Match the install-flow bump (60s → 180s).
      // The manual "Rebuild local database" pulls the same full
      // snapshot as the initial install and hit the same 60s cap
      // on slow WiFi + large menus.
      const snapRes = await axios.get(`${API}/sync/snapshot`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000,
      });
      await hydrateFromSnapshot(snapRes.data);
      // Restore prior simulation state.
      if (wasOffline) try { localStorage.setItem(DEV_SIMULATE_OFFLINE_KEY, "1"); } catch (_) { /* noop */ }
      if (wasCloud) try { localStorage.setItem(DEV_SIMULATE_CLOUD_KEY, "1"); } catch (_) { /* noop */ }
      const t = snapRes.data.totals || {};
      const total = Object.values(t).reduce((a, b) => a + Number(b || 0), 0);
      toast.success("Cloud sync complete", { description: `Refreshed ${total.toLocaleString()} records across ${Object.keys(t).length} collections.` });
      notifyChange();
    } catch (e) {
      toast.error("Sync failed", { description: e?.response?.data?.detail || e?.message });
    } finally { setBusy(null); }
  };

  const rebuild = async () => {
    if (!window.confirm("This will wipe the local database and re-download it from the cloud. Continue?")) return;
    setBusy("rebuild");
    try {
      const { wipeLocalDb } = await import("@/lib/localDb");
      await wipeLocalDb();
      try {
        if (typeof caches !== "undefined") {
          const keys = await caches.keys();
          await Promise.all(keys.filter((k) => k.startsWith("nfcs-")).map((k) => caches.delete(k)));
        }
      } catch (_) { /* noop */ }
      toast.success("Local database wiped");
      await forceSync();
    } catch (e) {
      toast.error("Rebuild failed", { description: e?.message });
    } finally { setBusy(null); }
  };

  // Session 27 — full "Reset Device" action, distinct from rebuild.
  //
  // Rebuild wipes the DB then immediately re-syncs from the current
  // session. Reset Device wipes EVERYTHING (local DB, offline creds,
  // sessions, cached device config, service-worker caches) and returns
  // the device to the install screen — the ONLY sanctioned way to
  // re-download the business snapshot after the initial install.
  //
  // Gated behind the DevTools PIN (0511) via the parent card, per the
  // Session-27 "install once, never re-download" rule.
  const resetDevice = async () => {
    const ok = window.confirm(
      "Reset this device?\n\n"
      + "This will:\n"
      + "  • Wipe the local database\n"
      + "  • Clear all offline PIN enrolments\n"
      + "  • Sign out of every role\n"
      + "  • Return this device to the install screen\n\n"
      + "This is the only way to re-download the business database. Continue?"
    );
    if (!ok) return;
    setBusy("reset");
    try {
      const { wipeLocalDb } = await import("@/lib/localDb");
      await wipeLocalDb();
      // Clear session storage (all role sessions).
      _clearAllRoleSessions();
      // Clear device config (business code, device role, install token).
      try {
        const keepKeys = ["nfcs.device_id", "nfcs.errors"]; // keep for diagnostics
        const removed = [];
        for (let i = 0; i < window.localStorage.length; i += 1) {
          const k = window.localStorage.key(i);
          if (k && (k.startsWith("nfcs.") || k.startsWith("nfcs:")) && !keepKeys.includes(k)) {
            removed.push(k);
          }
        }
        removed.forEach((k) => { try { window.localStorage.removeItem(k); } catch (_) { /* noop */ } });
      } catch (_) { /* noop */ }
      // Clear service-worker caches so the next launch cold-loads.
      try {
        if (typeof caches !== "undefined") {
          const keys = await caches.keys();
          await Promise.all(keys.filter((k) => k.startsWith("nfcs-")).map((k) => caches.delete(k)));
        }
      } catch (_) { /* noop */ }
      // Clear sessionStorage pending-install artefacts (Capacitor deep-link).
      try {
        window.sessionStorage.removeItem("nfcs.pendingInstallToken");
        window.sessionStorage.removeItem("nfcs.pendingInstallSource");
        window.sessionStorage.removeItem("nfcs.pendingInstallUrl");
      } catch (_) { /* noop */ }
      toast.success("Device reset", {
        description: "Returning to install screen…",
      });
      setTimeout(() => {
        try { window.location.assign("/install"); }
        catch (_) { try { window.location.href = "/install"; } catch (__) { /* noop */ } }
      }, 400);
    } catch (e) {
      toast.error("Reset failed", { description: e?.message });
      setBusy(null);
    }
  };

  const rerunReadiness = async () => {
    setBusy("readiness");
    try {
      const rep = await verifyOfflineReadiness();
      setReadinessDialog(rep);
    } finally { setBusy(null); }
  };

  const openEventLog = async () => {
    setBusy("event-log");
    try {
      const { readRecentEventLog } = await import("@/lib/localDb");
      const rows = await readRecentEventLog(100);
      setEventRows(rows);
      setEventLogOpen(true);
    } finally { setBusy(null); }
  };

  const exportDiagnostics = async () => {
    setBusy("export");
    try {
      const [info, ready] = await Promise.all([getLocalDbInfo(), verifyOfflineReadiness()]);
      const { readRecentEventLog } = await import("@/lib/localDb");
      const events = await readRecentEventLog(200);
      const blob = new Blob(
        [JSON.stringify({
          exported_at: new Date().toISOString(),
          user_agent: navigator.userAgent,
          location: window.location.href,
          service_worker_active: isServiceWorkerActive(),
          simulate_offline: localStorage.getItem(DEV_SIMULATE_OFFLINE_KEY) === "1",
          simulate_cloud_disconnect: localStorage.getItem(DEV_SIMULATE_CLOUD_KEY) === "1",
          local_db: info,
          readiness: ready,
          recent_events: events,
        }, null, 2)],
        { type: "application/json" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nfcs-diagnostics-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Diagnostics exported");
    } catch (e) {
      toast.error("Export failed", { description: e?.message });
    } finally { setBusy(null); }
  };

  return (
    <Card className="rounded-[24px] border-border bg-card" data-testid="settings-developer-tools-card">
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/15 text-orange-600 dark:text-orange-400">
              <Wrench className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold">Developer tools</p>
              <p className="text-xs text-muted-foreground">
                Maintenance, diagnostics and offline simulation.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4" data-testid="dev-tools-unlocked">
            {/* Simulation toggles */}
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex cursor-pointer items-center justify-between rounded-2xl border border-border bg-background/40 p-3 text-sm">
                <span>
                  <span className="font-medium">Simulate offline</span>
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    Rejects every http/fetch call. App must keep working from local data.
                  </span>
                </span>
                <Switch
                  checked={simulateOffline}
                  onCheckedChange={toggleSimulateOffline}
                  data-testid="dev-tools-simulate-offline"
                />
              </label>
              <label className="flex cursor-pointer items-center justify-between rounded-2xl border border-border bg-background/40 p-3 text-sm">
                <span>
                  <span className="font-medium">Simulate cloud disconnect</span>
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    Rejects /api/* only. Static assets and CDN images pass through.
                  </span>
                </span>
                <Switch
                  checked={simulateCloud}
                  onCheckedChange={toggleSimulateCloud}
                  data-testid="dev-tools-simulate-cloud"
                />
              </label>
            </div>

            {/* Action grid */}
            <div className="grid gap-2 sm:grid-cols-2">
              <Button
                variant="outline"
                className="justify-start rounded-xl"
                onClick={forceSync}
                disabled={busy === "sync" || busy === "rebuild"}
                data-testid="dev-tools-force-sync"
              >
                {busy === "sync" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />}
                Force cloud synchronisation
              </Button>
              <Button
                variant="outline"
                className="justify-start rounded-xl"
                onClick={rebuild}
                disabled={busy === "sync" || busy === "rebuild"}
                data-testid="dev-tools-rebuild-db"
              >
                {busy === "rebuild" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                Rebuild local database
              </Button>
              {/* Session 27.35 — Nuclear "clear cache & reload" option.
                  Wipes IndexedDB + sessionStorage completely without
                  needing a valid online token. Use when a device is
                  stuck on stale data (e.g. ghost pending orders that
                  don't match the cloud, zero-total receipts, corrupted
                  offline snapshot). After reload the app boots fresh
                  and can complete a normal install. No re-auth of
                  OTHER devices required — this only nukes THIS one. */}
              <Button
                variant="outline"
                className="justify-start rounded-xl border-destructive/50 text-destructive hover:bg-destructive/10"
                disabled={busy === "clear"}
                onClick={async () => {
                  if (!window.confirm("Clear ALL local data on this device and reload? Unsynced offline events will be lost.")) return;
                  setBusy("clear");
                  try {
                    const { wipeLocalDb } = await import("@/lib/localDb");
                    await wipeLocalDb();
                    try { window.sessionStorage.clear(); } catch (_) { /* noop */ }
                    // Keep localStorage business_code so the reinstall
                    // flow can re-hydrate the same business without
                    // asking for the code again.
                    try {
                      const keep = window.localStorage.getItem("nfcs.business_code");
                      window.localStorage.clear();
                      if (keep) window.localStorage.setItem("nfcs.business_code", keep);
                    } catch (_) { /* noop */ }
                    // Also unregister the service worker so a fresh
                    // shell is fetched on reload.
                    if (navigator.serviceWorker) {
                      try {
                        const regs = await navigator.serviceWorker.getRegistrations();
                        await Promise.all(regs.map((r) => r.unregister()));
                      } catch (_) { /* noop */ }
                    }
                    toast.success("Cache cleared. Reloading…");
                    setTimeout(() => window.location.reload(), 800);
                  } catch (e) {
                    setBusy("");
                    toast.error("Could not clear cache", { description: e?.message || "" });
                  }
                }}
                data-testid="dev-tools-clear-cache"
              >
                {busy === "clear" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Force clear cache & reload
              </Button>
              <Button
                variant="outline"
                className="justify-start rounded-xl"
                onClick={rerunReadiness}
                disabled={busy === "readiness"}
                data-testid="dev-tools-rerun-readiness"
              >
                {busy === "readiness" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Re-run offline readiness
              </Button>
              <Button
                variant="outline"
                className="justify-start rounded-xl"
                onClick={openEventLog}
                disabled={busy === "event-log"}
                data-testid="dev-tools-view-event-log"
              >
                <ClipboardList className="h-4 w-4" />
                View recent event log
              </Button>
              <Button
                variant="outline"
                className="justify-start rounded-xl"
                onClick={openCrashReports}
                disabled={busy === "crash"}
                data-testid="dev-tools-crash-reports"
              >
                {busy === "crash" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                Crash reports (all devices)
              </Button>
              <Button
                variant="outline"
                className="justify-start rounded-xl"
                onClick={exportDiagnostics}
                disabled={busy === "export"}
                data-testid="dev-tools-export-diagnostics"
              >
                {busy === "export" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export diagnostic information
              </Button>
              {/* Session 27 — Reset Device: full local wipe → back to
                  /install. Visually flagged as destructive so operators
                  don't hit it by accident. */}
              <Button
                variant="destructive"
                className="justify-start rounded-xl sm:col-span-2"
                onClick={resetDevice}
                disabled={busy === "reset"}
                data-testid="dev-tools-reset-device"
              >
                {busy === "reset" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                Reset device (return to install screen)
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              These tools take effect immediately. Restaurant users should not use them during service.
            </p>
          </div>
      </CardContent>

      {/* Recent event log dialog */}
      {/* Session 43 — crash reports from every device (server-side). */}
      <Dialog open={crashOpen} onOpenChange={setCrashOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-[24px] sm:max-w-3xl" data-testid="crash-reports-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Crash reports</DialogTitle>
            <DialogDescription>Uncaught errors shipped from every device of this business (web, tablets, APK, EXE). {crashData?.count_24h || 0} in the last 24 h. Each row carries the screen, device, app version, memory at the time and the last actions before it happened.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(crashData?.by_kind || {}).map(([k, v]) => <Badge key={k} variant="outline" className="rounded-full">{k}: {v}</Badge>)}
            <Button variant="ghost" className="ml-auto h-9 rounded-xl" onClick={openCrashReports} data-testid="crash-reports-refresh"><RefreshCw className="h-4 w-4" /> Refresh</Button>
            <Button variant="outline" className="h-9 rounded-xl text-destructive" onClick={clearCrashReports} data-testid="crash-reports-clear"><Trash2 className="h-4 w-4" /> Clear</Button>
          </div>
          {(crashData?.errors || []).length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="crash-reports-empty">No crash reports on the server yet. From now on every uncaught error on any device lands here automatically.</p>
          ) : (
            <div className="space-y-2" data-testid="crash-reports-list">
              {(crashData.errors || []).map((e) => (
                <div key={e.id} className="rounded-2xl border border-border bg-card p-3" data-testid={`crash-report-${e.id}`}>
                  <button type="button" className="flex w-full flex-wrap items-start gap-2 text-left" onClick={() => setCrashExpanded(crashExpanded === e.id ? null : e.id)}>
                    <Badge className={`rounded-full ${e.kind === "render" ? "bg-destructive/15 text-destructive hover:bg-destructive/15" : "bg-amber-500/15 text-amber-700 hover:bg-amber-500/15 dark:text-amber-300"}`}>{e.kind}</Badge>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{e.message || "(no message)"}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {e.created_at ? new Date(e.created_at).toLocaleString() : ""} · {e.platform || "web"}{e.app_version ? ` v${e.app_version}` : ""} · {e.role || "?"}{e.user ? ` (${e.user})` : ""} · {e.route || ""}
                        {e.memory?.used_mb ? ` · heap ${e.memory.used_mb}/${e.memory.limit_mb} MB` : ""}{e.online === false ? " · OFFLINE" : ""}
                      </span>
                    </span>
                  </button>
                  {crashExpanded === e.id ? (
                    <div className="mt-2 space-y-2 text-[11px]">
                      {e.stack ? <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-xl bg-secondary p-2">{e.stack}</pre> : null}
                      {e.component_stack ? <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-xl bg-secondary p-2">{e.component_stack}</pre> : null}
                      {(e.breadcrumbs || []).length ? <div><p className="font-semibold">Last actions</p><pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-xl bg-secondary p-2">{e.breadcrumbs.join("\n")}</pre></div> : null}
                      <p className="text-muted-foreground">Device {e.device_id || "?"} · {e.user_agent || ""}</p>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
          {(crashData?.local || []).length ? <p className="text-[11px] text-muted-foreground">This device also holds {crashData.local.length} local error record(s) — included in "Export diagnostic information".</p> : null}
        </DialogContent>
      </Dialog>
      <Dialog open={eventLogOpen} onOpenChange={setEventLogOpen}>
        <DialogContent className="max-w-2xl" data-testid="dev-tools-event-log-dialog">
          <DialogHeader>
            <DialogTitle>Recent event log</DialogTitle>
            <DialogDescription>
              Last {eventRows.length} events recorded on this device. Populated by the Local Event Engine (Session 26).
            </DialogDescription>
          </DialogHeader>
          {eventRows.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No events logged yet. Once the Local Event Engine is active, every restaurant action will appear here.
            </p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto rounded-2xl border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-secondary/60">
                  <tr>
                    <th className="p-2 text-left">When</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">User</th>
                  </tr>
                </thead>
                <tbody>
                  {eventRows.map((r, i) => (
                    <tr key={r.id || i} className="border-t border-border">
                      <td className="p-2 font-mono">{r.ts ? String(r.ts).slice(11, 19) : "—"}</td>
                      <td className="p-2">{r.event_type || "—"}</td>
                      <td className="p-2 font-mono text-[10px]">{r._meta?.status || "LOCAL"}</td>
                      <td className="p-2 text-muted-foreground">{r.user_name || r.user_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Readiness report dialog */}
      <Dialog open={!!readinessDialog} onOpenChange={(open) => { if (!open) setReadinessDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="dev-tools-readiness-dialog">
          <DialogHeader>
            <DialogTitle>Offline readiness</DialogTitle>
            <DialogDescription>
              {readinessDialog?.ok
                ? "All checks passed — this device is fully ready to operate offline."
                : "One or more checks failed. See details below."}
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1 rounded-2xl border border-border p-3 text-xs">
            {(readinessDialog?.checks || []).map((c, i) => {
              const isInfo = c.severity === "info";
              return (
                <li key={i} className="flex items-start gap-2">
                  {c.ok
                    ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    : isInfo
                      ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                      : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />}
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-[11px]">{c.name}</span>
                    {isInfo ? (
                      <span className="ml-1 rounded-full bg-secondary px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">info</span>
                    ) : null}
                    {c.detail ? <span className="ml-1 text-muted-foreground">— {c.detail}</span> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


/**
 * GatedSettingsPage — full-screen Developer PIN prompt that MUST be
 * cleared before the Settings page is visible. Requested in Session
 * 25.7 to keep restaurant users away from device configuration,
 * offline diagnostics AND developer maintenance in a single locked
 * area. The unlock is component-scoped state (not persisted): closing
 * the route or navigating away re-arms the gate.
 *
 * Uses the same PIN as the previous Developer Tools card (0511) so
 * support scripts and muscle memory don't need to change.
 */
export function GatedSettingsPage() {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const submit = (e) => {
    e?.preventDefault?.();
    if (pin === DEV_PIN) { setUnlocked(true); setError(null); setPin(""); }
    else { setError("Incorrect PIN."); setPin(""); }
  };
  if (unlocked) return <SettingsPage />;
  return (
    <main
      className="min-h-screen bg-[radial-gradient(1000px_circle_at_10%_0%,hsl(var(--primary)/0.14),transparent_55%),radial-gradient(700px_circle_at_100%_20%,hsl(var(--accent)/0.10),transparent_50%)] px-4 py-10 sm:px-6"
      data-testid="settings-gate"
    >
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md flex-col items-stretch justify-center gap-6">
        <button
          type="button"
          className="self-start text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={() => navigate(-1)}
          data-testid="settings-gate-back"
        >
          ← Back
        </button>
        <Card className="rounded-[28px] border-border bg-card">
          <CardContent className="space-y-5 p-6 sm:p-8">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
                <Lock className="h-6 w-6" />
              </div>
              <div>
                <h1 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">Device settings</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter the developer PIN to access this device's configuration, offline diagnostics and maintenance tools.
                </p>
              </div>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="settings-gate-form">
              <Input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                value={pin}
                onChange={(e) => { setPin(e.target.value); setError(null); }}
                placeholder="Enter PIN"
                maxLength={12}
                className="rounded-xl text-center text-lg tracking-[0.4em]"
                data-testid="settings-gate-pin-input"
              />
              {error ? (
                <p className="text-center text-xs text-red-600" data-testid="settings-gate-pin-error">{error}</p>
              ) : null}
              <Button type="submit" className="w-full rounded-xl" data-testid="settings-gate-submit">
                Unlock
              </Button>
            </form>
            <p className="text-center text-[11px] text-muted-foreground">
              Restaurant users don't need this — ask an administrator if you don't have the PIN.
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const [businessCode, setBusinessCodeState] = useState(() => getStoredBusinessCode());
  const [deviceRole, setDeviceRoleState] = useState(() => getStoredDeviceRole());
  const [deviceId] = useState(() => { try { return getDeviceId(); } catch (_) { return ""; } });
  const [online, setOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  const [checking, setChecking] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [standalone] = useState(() => isStandaloneLaunch());

  // Snapshot every persisted role-session on mount so we can (a) show
  // "Signed in as …" and (b) offer a "Sign out of all roles" action
  // even for users who arrived here from a link (no active dashboard).
  const [activeSessions, setActiveSessions] = useState([]);
  const refreshSessionSnapshot = useCallback(() => {
    const out = [];
    for (const role of Object.keys(ROLE_SESSION_KEYS)) {
      const sess = readRoleSession(role, "tab");
      if (sess && sess.token) {
        out.push({
          role,
          user: sess.user || sess.staff || sess.admin || null,
          business: sess.business || null,
        });
      }
    }
    setActiveSessions(out);
  }, []);
  useEffect(() => { refreshSessionSnapshot(); }, [refreshSessionSnapshot]);

  // Track connection state live — a service closing at the end of the
  // night on unreliable venue Wi-Fi will surface this on the Settings
  // page so ops knows to reconnect before Phase-2 sync features run.
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Best-effort "restaurant name" — pulled from the first available
  // session that carries it. Not persisted separately (that would be a
  // second source of truth and drift is inevitable).
  const restaurantName = useMemo(() => {
    for (const s of activeSessions) {
      if (s?.business?.name) return s.business.name;
    }
    return "";
  }, [activeSessions]);

  const primarySession = activeSessions[0] || null;
  const primaryUserLabel = (() => {
    const u = primarySession?.user;
    if (!u) return "Not signed in";
    // Users have varying shapes across roles (staff.name vs admin.username)
    return u.name || u.full_name || u.username || u.email || "Signed in";
  })();

  const checkForUpdates = async () => {
    setChecking(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) {
          try { await reg.update(); } catch (_) { /* noop */ }
        }
        toast.success("Checked for updates", {
          description: "If a new version is available, you'll see a reload prompt shortly.",
        });
      } else {
        toast.info("Service workers are not supported in this browser.");
      }
    } catch (error) {
      toast.error("Couldn't check for updates", { description: String(error?.message || error) });
    } finally {
      setChecking(false);
    }
  };

  const clearCache = async () => {
    setClearing(true);
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      }
      toast.success("Cache cleared", {
        description: "Reloading to fetch a fresh copy of the app…",
      });
      window.setTimeout(() => window.location.reload(), 900);
    } catch (error) {
      toast.error("Couldn't clear cache", { description: String(error?.message || error) });
    } finally {
      setClearing(false);
    }
  };

  const signOutAllRoles = async () => {
    // Session 27.115 — Clear local state FIRST so the user is never
    // trapped in a dead session. Server calls are best-effort.
    const sessionSnapshots = activeSessions.map((s) => {
      try {
        return readRoleSession(s.role, "tab");
      } catch (_) { return null; }
    }).filter(Boolean);

    _clearAllRoleSessions();
    refreshSessionSnapshot();
    toast.success("Signed out of all roles on this device.");
    const landing = resolveLandingPathForDevice();
    navigate(landing || "/", { replace: true });

    // Best-effort: tell the backend to purge each token.
    for (const sess of sessionSnapshots) {
      try {
        if (sess?.token) {
          await http.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${sess.token}` } })
            .catch(() => { /* backend errors are non-fatal */ });
        }
      } catch (e) { console.warn("[app] logout API call failed:", e?.message || e); }
    // Change the device's default role WITHOUT wiping the local business
    }
  };

  const switchDefaultDashboard = (newRole) => {
    // snapshot. This is the whole point of the local-first architecture:
    // any device can open any dashboard for its assigned business, and
    // switching roles must not force a re-download of the DB.
    try { setStoredDeviceRole(newRole); } catch (_) { /* noop */ }
    setDeviceRoleState(newRole);
    toast.success(`Default dashboard changed to ${newRole === "owner" ? "Owner / Manager" : newRole}`, {
      description: "Business data stays on this device — no re-download.",
    });
    // Navigate straight to the new role's login screen so the operator
    // can sign in immediately.
    navigate(`/${newRole}`);
  };

  const resetDeviceConfig = () => {
    try { clearDeviceConfig(); } catch (_) { /* noop */ }
    setBusinessCodeState("");
    setDeviceRoleState("");
    toast.success("Device role reset", {
      description: "Next launch will show the role picker again.",
    });
  };

  // Role → badge tone mirrors the global role colour system used across
  // the app (audit log dots, user rosters, install-page tiles).
  const roleBadgeTone = {
    owner:   "border-violet-500/40 bg-violet-500/10 text-violet-800 dark:text-violet-200",
    staff:   "border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200",
    kitchen: "border-orange-500/40 bg-orange-500/10 text-orange-800 dark:text-orange-200",
    bar:     "border-red-500/40 bg-red-500/10 text-red-800 dark:text-red-200",
    admin:   "border-sky-500/40 bg-sky-500/10 text-sky-800 dark:text-sky-200",
  };

  return (
    <main className="min-h-screen bg-background px-4 py-6 sm:px-6 sm:py-10" data-testid="settings-page">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        <header className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl"
            onClick={() => navigate(-1)}
            data-testid="settings-back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-['Space_Grotesk'] text-3xl font-semibold tracking-tight">Settings</h1>
            <p className="text-sm text-muted-foreground">Device & app configuration for this installation.</p>
          </div>
        </header>

        {/* Device summary card */}
        <Card className="rounded-[24px] border-border bg-card" data-testid="settings-device-card">
          <CardContent className="space-y-4 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
                <Settings className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold">Device</p>
                <p className="text-xs text-muted-foreground">This installation's identity.</p>
              </div>
            </div>

            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-border bg-background/40 p-3">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">App version</dt>
                <dd className="mt-1 font-mono text-sm" data-testid="settings-app-version">v{APP_VERSION}</dd>
              </div>
              <div className="rounded-2xl border border-border bg-background/40 p-3">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Device role</dt>
                <dd className="mt-1">
                  {deviceRole ? (
                    <Badge className={`border ${roleBadgeTone[deviceRole] || ""}`} data-testid="settings-device-role">
                      {deviceRole}
                    </Badge>
                  ) : (
                    <span className="text-sm text-muted-foreground" data-testid="settings-device-role">Not set</span>
                  )}
                </dd>
              </div>
              <div className="rounded-2xl border border-border bg-background/40 p-3">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Business code</dt>
                <dd className="mt-1 font-mono text-sm" data-testid="settings-business-code">
                  {businessCode || <span className="text-muted-foreground">—</span>}
                </dd>
              </div>
              <div className="rounded-2xl border border-border bg-background/40 p-3">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Restaurant</dt>
                <dd className="mt-1 text-sm" data-testid="settings-restaurant-name">
                  {restaurantName || <span className="text-muted-foreground">—</span>}
                </dd>
              </div>
              <div className="rounded-2xl border border-border bg-background/40 p-3">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Connection</dt>
                <dd className="mt-1 flex items-center gap-2 text-sm" data-testid="settings-connection">
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${online ? "bg-emerald-500" : "bg-red-500"}`} />
                  {online ? "Online" : "Offline"}
                </dd>
              </div>
              <div className="rounded-2xl border border-border bg-background/40 p-3">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Launched as</dt>
                <dd className="mt-1 text-sm" data-testid="settings-launch-mode">
                  {standalone ? "Installed PWA" : "Browser tab"}
                </dd>
              </div>
            </dl>

            <div className="rounded-2xl border border-dashed border-border p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Device ID</p>
              <p className="mt-1 font-mono text-xs text-muted-foreground" data-testid="settings-device-id">{deviceId || "—"}</p>
            </div>
          </CardContent>
        </Card>

        {/* Change default dashboard card — the user-visible surface for
            the "local-first" architecture. Any installed device can be
            re-assigned to any dashboard for its business, WITHOUT losing
            the local business database. Tapping a tile navigates to
            that role's PIN login screen (still requires a PIN). */}
        {businessCode ? (
          <Card className="rounded-[24px] border-border bg-card" data-testid="settings-role-switch-card">
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
                  <RefreshCw className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Default dashboard</p>
                  <p className="text-xs text-muted-foreground">Pick which dashboard opens first on this device. Any dashboard can still be reached from the login screen.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { key: "owner",   label: "Owner / Manager", icon: Users },
                  { key: "staff",   label: "Staff",           icon: ClipboardList },
                  { key: "kitchen", label: "Kitchen",         icon: ChefHat },
                  { key: "bar",     label: "Bar",             icon: Wine },
                ].map(({ key, label, icon: Icon }) => {
                  const isSel = deviceRole === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => switchDefaultDashboard(key)}
                      className={`flex flex-col items-center gap-2 rounded-2xl border p-3 text-sm font-medium transition-colors ${isSel ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/15 text-foreground ring-1 ring-[hsl(var(--accent))]" : "border-border bg-background/40 hover:bg-secondary"}`}
                      data-testid={`settings-set-default-${key}`}
                    >
                      <Icon className={`h-5 w-5 ${isSel ? "text-[hsl(var(--accent))]" : ""}`} />
                      <span className="text-center leading-tight">{label}</span>
                      {isSel ? <span className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--accent))]">Current</span> : null}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Business data stays on this device — switching does not re-download or wipe the local database.
              </p>
            </CardContent>
          </Card>
        ) : null}

        {/* Local Status card — offline readiness diagnostics
            (Session 25 Milestone A). */}
        <LocalStatusCard />

        {/* Developer Tools — PIN-protected diagnostics/maintenance
            surface (Session 25.6). Normal restaurant users cannot
            unlock this. */}
        <DeveloperToolsCard />

        {/* Active user card */}
        <Card className="rounded-[24px] border-border bg-card" data-testid="settings-user-card">
          <CardContent className="space-y-3 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold">Signed in</p>
                <p className="text-xs text-muted-foreground">Active sessions on this tab.</p>
              </div>
            </div>

            {activeSessions.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="settings-no-sessions">
                {primaryUserLabel}
              </p>
            ) : (
              <ul className="space-y-2">
                {activeSessions.map((s) => (
                  <li
                    key={s.role}
                    className="flex items-center justify-between rounded-2xl border border-border bg-background/40 px-3 py-2"
                    data-testid={`settings-session-${s.role}`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge className={`border ${roleBadgeTone[s.role] || ""}`}>{s.role}</Badge>
                      <span className="text-sm">
                        {s.user?.name || s.user?.full_name || s.user?.username || s.user?.email || "Signed in"}
                      </span>
                    </div>
                    {s.business?.name && (
                      <span className="text-xs text-muted-foreground">{s.business.name}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Actions card */}
        <Card className="rounded-[24px] border-border bg-card" data-testid="settings-actions-card">
          <CardContent className="space-y-3 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
                <RefreshCw className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold">Maintenance</p>
                <p className="text-xs text-muted-foreground">Keep this installation healthy.</p>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full justify-start rounded-xl"
              onClick={checkForUpdates}
              disabled={checking}
              data-testid="settings-check-updates"
            >
              <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
              {checking ? "Checking…" : "Check for updates"}
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start rounded-xl"
              onClick={clearCache}
              disabled={clearing}
              data-testid="settings-clear-cache"
            >
              <Trash2 className="h-4 w-4" />
              {clearing ? "Clearing…" : "Clear cache & reload"}
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start rounded-xl"
              onClick={resetDeviceConfig}
              data-testid="settings-reset-role"
            >
              <Package className="h-4 w-4" />
              Reset device role
            </Button>

            <Button
              variant="destructive"
              className="w-full justify-start rounded-xl"
              onClick={signOutAllRoles}
              disabled={activeSessions.length === 0}
              data-testid="settings-signout-all"
            >
              <LogOut className="h-4 w-4" />
              Sign out of all roles
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          NFC Solutions · Secure. Smart. Connected.
        </p>
      </div>
    </main>
  );
}
